export interface SelectOption<T> {
  label: string;
  value: T;
}

export const LOCALE_OPTIONS: SelectOption<string>[] = [
  { label: 'English', value: 'en-US' },
  { label: 'Portuguese', value: 'pt-PT' },
];

export const DATE_FORMAT_OPTIONS: SelectOption<string>[] = [
  { label: 'MM/dd/yyyy', value: 'MM/dd/yyyy' },
  { label: 'dd/MM/yyyy', value: 'dd/MM/yyyy' },
  { label: 'yyyy/MM/dd', value: 'yyyy/MM/dd' },
];

export const TIME_FORMAT_OPTIONS: SelectOption<boolean>[] = [
  { label: '12-hour clock', value: false },
  { label: '24-hour clock', value: true },
];

export const FALLBACK_TIME_ZONES = [
  'GMT',
  'UTC',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Asia/Tokyo',
  'Asia/Dubai',
  'Australia/Sydney',
];

export function getTimeZoneOptions(): SelectOption<string>[] {
  const intlWithSupportedValues: {
    supportedValuesOf?: (
      key:
        | 'calendar'
        | 'collation'
        | 'currency'
        | 'numberingSystem'
        | 'timeZone'
        | 'unit',
    ) => string[];
  } = Intl;

  const supportedValues = intlWithSupportedValues.supportedValuesOf;
  if (supportedValues) {
    const options = supportedValues('timeZone');
    if (options.length > 0)
      return options.map(value => ({ label: value, value }));
  }

  return FALLBACK_TIME_ZONES.map(value => ({ label: value, value }));
}

export function getLocaleName(locale: string): string {
  return (
    LOCALE_OPTIONS.find(option => option.value === locale)?.label || locale
  );
}

export function getDateFormatDisplayName(dateFormat?: string | null): string {
  if (!dateFormat) return '';

  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const yyyy = String(now.getFullYear());

  const example = dateFormat
    .replace('MM', mm)
    .replace('dd', dd)
    .replace('yyyy', yyyy);

  return `${dateFormat} (e.g.: ${example})`;
}

function formatUtcOffset(rawOffset: string): string {
  if (rawOffset === 'GMT' || rawOffset === 'UTC') return 'UTC+00:00';

  const match = rawOffset.match(/^GMT([+-])(\d{1,2}):(\d{2})$/);
  if (!match) return rawOffset.replace('GMT', 'UTC');

  const [, sign, hours, minutes] = match;
  return `UTC${sign}${hours.padStart(2, '0')}:${minutes}`;
}

function getGenericTimeZoneName(ianaZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaZone,
      timeZoneName: 'genericLong' as Intl.DateTimeFormatOptions['timeZoneName'],
    }).formatToParts(new Date());
    const name = parts.find(p => p.type === 'timeZoneName')?.value;
    if (name) return name;
  } catch {
    // ignore error
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ianaZone,
    timeZoneName: 'long',
  }).formatToParts(new Date());
  const name = parts.find(p => p.type === 'timeZoneName')?.value ?? ianaZone;
  return name
    .replace(' Standard Time', ' Time')
    .replace(' Daylight Time', ' Time')
    .replace(' Summer Time', ' Time');
}

export function getTimeZoneDisplayName(timeZone?: string | null): string {
  if (!timeZone) return '';

  try {
    const offsetParts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date());
    const rawOffset =
      offsetParts.find(p => p.type === 'timeZoneName')?.value ?? '';
    const utcOffset = formatUtcOffset(rawOffset);
    const genericName = getGenericTimeZoneName(timeZone);

    return `(${utcOffset}) ${genericName}`;
  } catch {
    return timeZone;
  }
}
